'use client';

import { produce } from 'immer';
import { FC, ReactNode, useEffect } from 'react';
import shallow from 'zustand/shallow';

import {
  PageProductListingFilters,
  PageProductListingOptions,
} from 'src/data/Contentful/ContentfulGetPageProductListing.types';
import { Skeleton } from 'src/general/components/Skeleton/Skeleton';
import { pluralise } from 'src/utils/pluralise';

import { storeFactory } from './storeFactory';

type ResultsCount = number | null;

interface PlpStoreStateData {
  currentFilters: PageProductListingFilters;
  currentListingOptions: PageProductListingOptions;
  totalResults: ResultsCount;
  driveTrainModalOpen: boolean;
  filtersModalOpen: boolean;
}

interface PlpStoreStateFunctions {
  setResults: (
    totalResults: ResultsCount,
    filters: PageProductListingFilters,
    listingOptions: PageProductListingOptions,
  ) => void;
  setDriveTrainModalOpen: (value: boolean) => void;
  setFiltersModalOpen: (value: boolean) => void;
}

const {
  store: plpStore,
  StoreProvider: PlpStoreProvider,
  useCreateStore: useCreatePlpStore,
} = storeFactory<PlpStoreStateData, PlpStoreStateFunctions>(
  () => ({
    totalResults: null,
    currentFilters: {},
    currentListingOptions: {},
    driveTrainModalOpen: false,
    filtersModalOpen: false,
  }),
  (set) => ({
    setResults: (
      totalResults: ResultsCount,
      filters: PageProductListingFilters,
      listingOptions: PageProductListingOptions,
    ) => {
      set((state) =>
        produce(state, (draft) => {
          draft.totalResults = totalResults;
          draft.currentFilters = filters;
          draft.currentListingOptions = listingOptions;
        }),
      );
    },

    setDriveTrainModalOpen: (value: boolean) => {
      set((state) =>
        produce(state, (draft) => {
          draft.driveTrainModalOpen = value;
        }),
      );
    },
    setFiltersModalOpen: (value: boolean) => {
      set((state) =>
        produce(state, (draft) => {
          draft.filtersModalOpen = value;
        }),
      );
    },
  }),
);

export { plpStore };

export const PlpStore: FC<{ children: ReactNode }> = ({ children }) => {
  const createPlpStore = useCreatePlpStore({});

  return <PlpStoreProvider createStore={createPlpStore}>{children}</PlpStoreProvider>;
};

export const PlpContextSet: FC<
  Pick<PlpStoreStateData, 'totalResults' | 'currentFilters' | 'currentListingOptions'>
> = ({ totalResults, currentFilters, currentListingOptions }) => {
  const { setResults } = plpStore(
    (state) => ({
      setResults: state.setResults,
    }),
    shallow,
  );

  useEffect(() => {
    setResults(totalResults, currentFilters, currentListingOptions);
  }, [totalResults]);

  return null;
};

export const PlpContextTotalResults: FC = () => {
  const { totalResults } = plpStore(
    (state) => ({
      totalResults: state.totalResults,
    }),
    shallow,
  );

  if (totalResults === null) {
    return <Skeleton size="5em" />;
  }

  return (
    <>
      (<span data-testid="PlpHeader-component-count">{totalResults}</span>{' '}
      {pluralise('result', totalResults, { skipNumber: true })})
    </>
  );
};
